import { useEffect } from "react"
import APIUrls from "../services/APIUrls"
import axiosInstance from "../services/axiosInstance"
import InContextUtil from "../services/InContextUtil"
import Constants from "../Constants";

interface EditIcrProps {
    projectId: string
    parentHttpDomain: string
    targetPreviewId?: string
    iframeLoaded?: boolean
    updateReviewStatus?: (status: boolean) => void
    isReviewCompleted?: boolean
    onCountUpdate: (newCount: number) => void
}

export default function EditIcr({
    projectId,
    parentHttpDomain,
    targetPreviewId = "target-preview",
    iframeLoaded,
    updateReviewStatus,
    isReviewCompleted,
    onCountUpdate
}: EditIcrProps) {


    useEffect(() => {
        if (iframeLoaded && !isReviewCompleted) {
            updateICRWithEditSegements(projectId)
        }

    }, [projectId,iframeLoaded])

    function updateICRWithEditSegements(projectId: string) {
        axiosInstance
            .get(APIUrls.PROJECT_TRANSLATED_FILE_UNCOMMITTED_TRANSLATION_UNITS(projectId))
            .then((res) => {
                const uncommitedTus = res?.data
                uncommitedTus.forEach((segment) => {
                    updatePreviewWithSegment(segment, false)
                })
            })
            .catch((err) => {
                console.error(err)
            })
    }

    function updatePreviewWithSegment(segment: any, lblEdit: boolean) {
        segment.latestTranslation = segment.translations[segment.translations.length - 1]
        segment.latestTranslation.tokenizedText = InContextUtil.tokenizeSegmentContent(
            segment.latestTranslation.rawText,
            Constants.STANDARD,
        )

        const syncedTranslations = lblEdit
            ? segment.translations.filter((t: any) => t.isMostRecent === false)
            : segment.translations.filter((t: any) => t.synced === true)

        let syncedTuSnippets: any[] = []
        if (syncedTranslations.length > 0) {
            const latestSyncedTranslation = syncedTranslations[syncedTranslations.length - 1]
            syncedTuSnippets = InContextUtil.tokenizeSegmentContent(latestSyncedTranslation.rawText,  Constants.STANDARD)
        }

        messageSegmentUpdateToInContext(segment.docSegPos, segment.latestTranslation, syncedTuSnippets, false)
    }

    function messageSegmentUpdateToInContext(
        docSegPos: string,
        translation: any,
        syncedTuSnippets: any[],
        isToken: boolean,
    ) {
        const message = {
            action: "replace",
            docSegPos: docSegPos,
            syncedTuSnippets: syncedTuSnippets,
            isToken: isToken,
            newRawText: translation.rawText,
            newTokenizedText: translation.tokenizedText,
            };

        const iframe = document.getElementById(targetPreviewId) as HTMLIFrameElement | null;
        if (iframe && iframe.contentWindow) {
            iframe.contentWindow.postMessage(JSON.stringify(message), '*');
        }
    }

    useEffect(() => {
        function handleEditActions(e: MessageEvent) {
            if (!e.data) return

            let message
            try {
                message =
                    e.type === "webpackInvalid" ||
                        (typeof e.data === "object" && e.data.type === "webpackWarnings") ||
                        (typeof e.data === "object" && e.data.type === "webpackClose")
                        ? e.data
                        : JSON.parse(e.data)
            } catch (err) {
                // Silent out webpack related exceptions
                return
            }

            // Handle bookmark creation
            if (message.action === "FETCH_TU_TO_EDIT") {
                axiosInstance
                .get(APIUrls.SHOW_TRANSLATION_HISTORY(message.projectId, message.docSegPos))
                    .then((res) => {
                        const tu = res?.data                        
                        const isToken = false

                        const tokenizedText = InContextUtil.tokenizeSegmentContent(
                            tu.translations[tu.translations.length - 1].rawText,
                             Constants.STANDARD,
                        )

                        let syncedTuSnippets: any[] = []
                        let syncedTranslations = tu.translations.filter((t: any) => t.synced === true)

                        if(syncedTranslations.length == 0 && tokenizedText.length == 1){
                            syncedTranslations =tokenizedText;
                        }

                        if (syncedTranslations.length > 0) {
                            const latestSyncedTranslation = syncedTranslations[syncedTranslations.length - 1]
                            syncedTuSnippets = InContextUtil.tokenizeSegmentContent(latestSyncedTranslation.rawText,  Constants.STANDARD)

                            // Set empty node for the blanked nodes
                            const initialTuSnippets = InContextUtil.tokenizeSegmentContent(tu.translations[0].rawText,  Constants.STANDARD)
                            if (syncedTuSnippets.length < initialTuSnippets.length) {
                                for (let i = 0; i < initialTuSnippets.length; i++) {
                                    if (!initialTuSnippets[i].isTag && syncedTuSnippets[i]?.isTag) {
                                        syncedTuSnippets.splice(i, 0, {
                                            text: "",
                                            isTag: false,
                                            tokenizedSnippet: [],
                                            isEmpty: true,
                                        })
                                    }
                                }
                            }
                        }

                          const message = {
                            data: tokenizedText,
                            tuId: tu.docSegPos,
                            syncedTuSnippets: syncedTuSnippets, 
                            isToken: isToken,
                            action: "ADJUST_HTML_FOR_EDIT"
                          };
                        const iframe = document.getElementById(targetPreviewId) as HTMLIFrameElement | null;
                        if (iframe && iframe.contentWindow) {
                            iframe.contentWindow.postMessage(JSON.stringify(message), '*');
                        }


                    })
                    .catch((err) => {
                        console.error("Error fetching translation history:", err)
                    })
            }

            if (message.action === "SAVE_EDITED_TRANSLATION") {
                const payload: any = {
                    rawText: message.rawText
                };

                // include optional metadata if present
                if (message.feedbackCategory !== undefined) payload.feedbackCategory = message.feedbackCategory;
                if (message.feedbackSeverity !== undefined) payload.feedbackSeverity = message.feedbackSeverity;

                console.log("Saving edit with payload:", payload);

                axiosInstance
                    .post(APIUrls.ADD_FILE_TRANSLATION(message.projectTranslationAssetId, message.docSegPos), payload)
                    .then((res) => {
                        const newCount = res.data;
                        onCountUpdate(newCount);
                        updateReviewStatus?.(false);

                        if (message.isLineByLine) {
                            updateICRWithEditSegements(message.projectTranslationAssetId)
                        }
                    })
                    .catch((err) => {
                        console.error("Error saving translation:", err);
                        console.error("Error response:", err.response?.data);
                    })
            }
        }

        // Add event listener for message events
        window.addEventListener("message", handleEditActions)

        // Clean up event listener on component unmount
        return () => {
            window.removeEventListener("message", handleEditActions)
        }
    }, [onCountUpdate])

    return null
}

